import React, { useState } from 'react';

export interface BaseExercise {
  id: string;
  name: string;
  description: string;
  type: 'pose' | 'strength' | 'interval' | 'cardio' | 'plyometric' | 'flexibility';
  duration?: number; // seconds
  repetitions?: number;
  sets?: number;
  weight?: string;
  distance?: string;
  intensity?: 'low' | 'moderate' | 'high' | 'max';
  restPeriod?: number; // seconds
  instructions?: string[];
  benefits?: string[];
  modifications?: string[];
  precautions?: string[];
  images?: string[];
  videos?: string[];
  equipment?: string[];
  targetMuscles?: string[];
  // Interval-specific
  intervals?: {
    type: 'work' | 'rest' | 'warmup' | 'cooldown';
    duration: number;
    intensity?: string;
    description?: string;
  }[];
}

interface ExerciseCardProps {
  exercise: BaseExercise;
  index: number;
  expandedId: string | null;
  onToggleExpansion: (id: string) => void;
  variant?: 'mobility' | 'strength' | 'cardio' | 'interval';
}

// Image Modal Component
const ImageModal: React.FC<{
  imageUrl: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ imageUrl, alt, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="relative max-w-full max-h-full">
        <img 
          src={imageUrl} 
          alt={alt}
          className="max-w-full max-h-full object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export const ExerciseCard: React.FC<ExerciseCardProps> = ({
  exercise,
  index,
  expandedId,
  onToggleExpansion,
  variant = 'mobility'
}) => {
  const isExpanded = expandedId === exercise.id;
  const keyInstruction = exercise.instructions?.[0] || exercise.description;
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const getVariantColors = () => {
    switch (variant) {
      case 'strength':
        return {
          gradient: 'from-red-100 to-orange-100',
          badge: 'bg-red-200',
          badgeText: 'text-red-700',
          accent: 'bg-red-100 text-red-700'
        };
      case 'cardio':
        return {
          gradient: 'from-blue-100 to-cyan-100',
          badge: 'bg-blue-200',
          badgeText: 'text-blue-700',
          accent: 'bg-blue-100 text-blue-700'
        };
      case 'interval':
        return {
          gradient: 'from-yellow-100 to-orange-100',
          badge: 'bg-yellow-200',
          badgeText: 'text-yellow-700',
          accent: 'bg-yellow-100 text-yellow-700'
        };
      default: // mobility
        return {
          gradient: 'from-purple-100 to-blue-100',
          badge: 'bg-purple-200',
          badgeText: 'text-purple-700',
          accent: 'bg-purple-100 text-purple-700'
        };
    }
  };

  const colors = getVariantColors();

  const renderCompactInfo = () => {
    const info: string[] = [];
    
    // 🆕 JAN 29, 2026: Detect if weight field contains duration string (e.g., "30-40 s/side")
    const rawReps = exercise.weight;
    const isDuration = rawReps && typeof rawReps === 'string' && (
      rawReps.includes(' s') || rawReps.includes('sec') || rawReps.includes('seconds')
    );
    
    if (isDuration) {
      // Timed exercise: show duration string (e.g., "30s/side")
      info.push(rawReps.replace(' s', 's').replace(' sec', 's'));
    } else {
      // Regular exercise: show reps
      if (exercise.duration) info.push(`${exercise.duration}s`);
      if (exercise.repetitions) info.push(`${exercise.repetitions}x`);
    }
    
    if (exercise.sets) info.push(`${exercise.sets} sets`);
    if (exercise.weight && !isDuration) info.push(exercise.weight); // Only show weight if not duration string
    if (exercise.distance) info.push(exercise.distance);
    if (exercise.intensity) info.push(exercise.intensity);
    
    return info.length > 0 ? info.join(' • ') : 'Hold';
  };

  const handleImageClick = (imageUrl: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const renderIntervalBlocks = () => {
    if (!exercise.intervals) return null;

    return (
      <div className="space-y-2">
        <h4 className="font-medium text-gray-900 mb-2 text-sm">Interval Structure</h4>
        <div className="space-y-1">
          {exercise.intervals.map((interval, idx) => (
            <div key={idx} className={`flex items-center justify-between p-2 rounded text-sm ${
              interval.type === 'work' ? 'bg-red-50 border border-red-200' :
              interval.type === 'rest' ? 'bg-blue-50 border border-blue-200' :
              interval.type === 'warmup' ? 'bg-yellow-50 border border-yellow-200' :
              'bg-green-50 border border-green-200'
            }`}>
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${
                  interval.type === 'work' ? 'bg-red-500' :
                  interval.type === 'rest' ? 'bg-blue-500' :
                  interval.type === 'warmup' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`} />
                <span className="font-medium capitalize">{interval.type}</span>
                {interval.description && (
                  <span className="text-gray-600">- {interval.description}</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium">{interval.duration}s</span>
                {interval.intensity && (
                  <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                    {interval.intensity}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTargetMuscles = () => {
    if (!exercise.targetMuscles || exercise.targetMuscles.length === 0) return null;

    return (
      <div className="bg-gray-50 rounded-lg p-3">
        <h5 className="font-medium text-gray-800 mb-1 text-sm">Target Muscles</h5>
        <div className="flex flex-wrap gap-1">
          {exercise.targetMuscles.map((muscle, idx) => (
            <span key={idx} className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
              {muscle}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderEquipment = () => {
    if (!exercise.equipment || exercise.equipment.length === 0) return null;

    return (
      <div className="bg-gray-50 rounded-lg p-3">
        <h5 className="font-medium text-gray-800 mb-1 text-sm">Equipment</h5>
        <div className="flex flex-wrap gap-1">
          {exercise.equipment.map((item, idx) => (
            <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Primary image for compact view
  const primaryImage = exercise.images?.[0];
  const hasAdditionalImages = exercise.images && exercise.images.length > 1;

  return (
    <>
      <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm mb-2">
        {/* Compact Header - Always Visible */}
        {/* 🔧 JAN 29, 2026: HEADER LAYOUT OVERHAUL - Image RIGHT, Info LEFT with quality text */}
        <div 
          className="flex items-start justify-between py-2 px-3 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => onToggleExpansion(exercise.id)}
        >
          {/* LEFT: Info Column */}
          <div className="flex-1 mr-4 min-w-0">
            {/* Line 1: Exercise Name */}
            <h3 className="font-semibold text-gray-900 mb-1">
              {exercise.name}
            </h3>
            
            {/* Line 2: Target Muscles */}
            {exercise.targetMuscles && exercise.targetMuscles.length > 0 && (
              <p className="text-sm text-gray-600 capitalize mb-1">
                {exercise.targetMuscles.join(' • ')}
              </p>
            )}
            
            {/* Line 3: Quality Instruction (exercise.description) */}
            {exercise.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                {exercise.description}
              </p>
            )}
            
            {/* Line 4: Compact Sets/Reps Info */}
            <p className="text-xs text-gray-500">
              {renderCompactInfo()}
            </p>
          </div>
          
          {/* RIGHT: Image + Chevron Column */}
          <div className="flex items-start gap-3 flex-shrink-0">
            {/* Exercise Image (20x20) or Numbered Circle */}
            <div className="relative">
              {primaryImage ? (
                <div 
                  className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={(e) => handleImageClick(primaryImage, e)}
                >
                  <img 
                    src={primaryImage} 
                    alt={`${exercise.name} demonstration`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to gradient background if image fails (safe DOM manipulation)
                      const target = e.currentTarget;
                      const parent = target.parentElement;
                      if (parent) {
                        while (parent.firstChild) parent.removeChild(parent.firstChild);
                        const outer = document.createElement('div');
                        outer.className = `w-20 h-20 bg-gradient-to-br ${colors.gradient} rounded-lg flex items-center justify-center`;
                        const inner = document.createElement('div');
                        inner.className = `w-8 h-8 ${colors.badge} rounded-full flex items-center justify-center`;
                        const span = document.createElement('span');
                        span.className = `text-sm font-bold ${colors.badgeText}`;
                        span.textContent = String(index + 1);
                        inner.appendChild(span);
                        outer.appendChild(inner);
                        parent.appendChild(outer);
                      }
                    }}
                  />
                  {/* Image count indicator */}
                  {hasAdditionalImages && (
                    <div className="absolute bottom-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1.5 py-0.5 rounded">
                      +{exercise.images!.length - 1}
                    </div>
                  )}
                </div>
              ) : (
                // Fallback to numbered circle (larger for 20x20)
                <div className={`w-20 h-20 bg-gradient-to-br ${colors.gradient} rounded-lg flex items-center justify-center`}>
                  <div className={`w-8 h-8 ${colors.badge} rounded-full flex items-center justify-center`}>
                    <span className={`text-sm font-bold ${colors.badgeText}`}>{index + 1}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Expand/Collapse Chevron */}
            <div className="flex-shrink-0">
              <div className={`w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="p-3 space-y-3">
              {/* How To Perform - Movement Cycle Description */}
              {exercise.description && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-1.5 text-sm">How to Perform</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">{exercise.description}</p>
                </div>
              )}

              {/* Exercise Details Grid - 🆕 JAN 29, 2026: Smart duration vs reps detection */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                {exercise.sets && (
                  <div className="bg-white rounded-lg p-2">
                    <span className="font-medium text-gray-700">Sets:</span>
                    <span className="ml-2 text-gray-900">{exercise.sets}</span>
                  </div>
                )}
                {/* 🆕 JAN 29, 2026: Detect if this is duration-based (timed holds like planks) */}
                {(() => {
                  const rawReps = exercise.weight; // Raw reps string stored in weight field
                  const isDuration = rawReps && typeof rawReps === 'string' && (
                    rawReps.includes(' s') || rawReps.includes('sec') || rawReps.includes('seconds')
                  );
                  
                  if (isDuration) {
                    // Show as duration: "30-40s/side" (NOT "30x")
                    return (
                      <div className="bg-white rounded-lg p-2">
                        <span className="font-medium text-gray-700">Duration:</span>
                        <span className="ml-2 text-gray-900">{rawReps.replace(' s', 's').replace(' sec', 's')}</span>
                      </div>
                    );
                  } else if (exercise.repetitions) {
                    // Show as reps: "8x"
                    return (
                      <div className="bg-white rounded-lg p-2">
                        <span className="font-medium text-gray-700">Reps:</span>
                        <span className="ml-2 text-gray-900">{exercise.repetitions}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {exercise.restPeriod && (
                  <div className="bg-white rounded-lg p-2">
                    <span className="font-medium text-gray-700">Rest:</span>
                    <span className="ml-2 text-gray-900">{exercise.restPeriod}s</span>
                  </div>
                )}
                {/* Only show Weight if it's NOT the raw reps string (not duration) */}
                {exercise.weight && !exercise.weight.includes(' s') && !exercise.weight.includes('sec') && (
                  <div className="bg-white rounded-lg p-2">
                    <span className="font-medium text-gray-700">Weight:</span>
                    <span className="ml-2 text-gray-900">{exercise.weight}</span>
                  </div>
                )}
                {exercise.distance && (
                  <div className="bg-white rounded-lg p-2">
                    <span className="font-medium text-gray-700">Distance:</span>
                    <span className="ml-2 text-gray-900">{exercise.distance}</span>
                  </div>
                )}
              </div>

              {/* Interval Structure (for interval exercises) */}
              {exercise.intervals && renderIntervalBlocks()}

              {/* Equipment (Target Muscles removed - already in header) */}
              {/* 🔧 JAN 29, 2026: Visual Guide section removed - header image is clickable */}
              <div>
                {renderEquipment()}
              </div>

              {/* Form Cues - Coaching Points */}
              {exercise.instructions && exercise.instructions.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-1.5 text-sm">Form Cues</h4>
                  <ul className="space-y-1.5">
                    {exercise.instructions.map((instruction: string, idx: number) => (
                      <li key={idx} className="flex text-sm">
                        <span className="text-gray-500 mr-2">•</span>
                        <span className="text-gray-700">{instruction}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 🆕 JAN 29, 2026: Safety Section - Full-width yellow-shaded (NOT grid column) */}
              {exercise.precautions && exercise.precautions.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⚠️</span>
                    <div className="flex-1">
                      <h4 className="font-medium text-yellow-900 mb-1.5 text-sm">Safety</h4>
                      <ul className="space-y-1">
                        {exercise.precautions.map((precaution: string, idx: number) => (
                          <li key={idx} className="flex text-sm text-yellow-800">
                            <span className="text-yellow-600 mr-2">•</span>
                            <span>{precaution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Benefits & Modifications (2-column, NOT 3-column) */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {exercise.benefits && exercise.benefits.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <h5 className="font-medium text-green-800 mb-1">Benefits</h5>
                    <ul className="space-y-1">
                      {exercise.benefits.slice(0, 2).map((benefit: string, idx: number) => (
                        <li key={idx} className="text-green-700">• {benefit}</li>
                      ))}
                      {exercise.benefits.length > 2 && (
                        <li className="text-green-600">+{exercise.benefits.length - 2} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {exercise.modifications && exercise.modifications.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <h5 className="font-medium text-blue-800 mb-1">Modify</h5>
                    <ul className="space-y-1">
                      {exercise.modifications.slice(0, 2).map((mod: string, idx: number) => (
                        <li key={idx} className="text-blue-700">• {mod}</li>
                      ))}
                      {exercise.modifications.length > 2 && (
                        <li className="text-blue-600">+{exercise.modifications.length - 2} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage}
          alt={`${exercise.name} demonstration`}
          isOpen={imageModalOpen}
          onClose={() => {
            setImageModalOpen(false);
            setSelectedImage(null);
          }}
        />
      )}
    </>
  );
}; 